import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { icModel } from 'generated-ts-common';
import type { FormIOSchema } from '@formio/react';
import { Form } from '@formio/react';
import type { Form as FormioForm } from 'formiojs';
import type { DynamicRequestOptions, SuccessResponse } from '../../lib';
import { showError } from '../../lib';
import type { DebouncedFunc } from 'lodash';
import { debounce, isEqual } from 'lodash';

type FormInstance = Omit<
  icModel.saf.infrastructure.model.form.FormInstance,
  'id'
>;
type FormInstanceContent = icModel.saf.infrastructure.model.form.FormInstanceContent;

export type FormioProps = {
  readonly postOrPutFormInstance: (
    params: {
      formInstance: FormInstance;
    } & DynamicRequestOptions
  ) => Promise<SuccessResponse<string>>;
  readonly value: FormInstance;
  readonly onChange: (value: FormInstance) => void;
  readonly url: string;
  readonly readOnly: boolean;
  readonly debounceWait?: number;
};

const _currentAppLanguage: string = window.SenseConstants?.language ?? 'en';

/**
 * Compares the previous with the next properties.
 *
 * This ignores the two functions 'postOrPutFormInstance' and 'onChange'
 * @param prevProps the previous properties
 * @param nextProps the new properties
 */
function formioContainerPropsAreEqual(
  prevProps: FormioProps,
  nextProps: FormioProps
): boolean {
  //we ignore the changes of the functions (postOrPutFormInstance)
  return (
    prevProps.url === nextProps.url &&
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.debounceWait === nextProps.debounceWait &&
    prevProps.onChange === nextProps.onChange
  );
}

/**
 *
 * @param url the url for the requests
 * @param postOrPutFormInstance the function that creates or updates a form-instance
 * @param value the {@link FormInstance} or {@link icModel.saf.infrastructure.model.form.FormTemplate}
 * @param onChange when the {@link FormInstance} changed
 * @param loading if it is loading
 * @param readOnly weather or not the Form is read only
 */
export const FormioContainer = React.memo(function FormioContainer({
  postOrPutFormInstance,
  url,
  value,
  onChange,
  readOnly,
  debounceWait
}: FormioProps): React.JSX.Element {
  const currentAppLanguage = useRef(_currentAppLanguage);
  const postOrPutReference = useRef(
    debounce(postOrPutFormInstance, debounceWait ?? 1000, { trailing: true })
  );
  const updateFunc = useRef(update);
  const [formReady, setFormReady] = useState(true);

  const options = useMemo(
    () =>
      ({
        language: currentAppLanguage.current,
        i18n: {
          [currentAppLanguage.current]: { ...(window?.EHSLanguageMap ?? {}) }
        },
        readOnly: readOnly
      } as const),
    [currentAppLanguage, readOnly]
  );
  const onChangeWrapper = useCallback(
    async (record: FormInstanceContent) => {
      if (!formReady) {
        return;
      } //VERY IMPORTANT otherwise you get a mismatch in the update (stale closure, ...)
      console.debug(record);
      const result = await updateFunc
        .current(value, record, postOrPutReference.current, url)
        .catch(showError);
      if (!result) {
        return;
      }
      onChange(result);
    },
    [formReady, onChange, url, value]
  );
  return useMemo(() => {
    const tempValue = value;
    let submission: FormInstanceContent;
    if ('content' in tempValue && 'data' in tempValue.content) {
      submission = tempValue.content;
    } else {
      submission = { data: {}, isValid: true, changed: {} };
    }

    function setFormReadyWrapper(instance: FormioForm) {
      setFormReady(instance != null);
    }

    return (
      <div className="formio-container" data-testid="form">
        <Form
          form={value.schema as FormIOSchema}
          submission={submission}
          options={options}
          onChange={onChangeWrapper}
          formReady={setFormReadyWrapper}
        />
      </div>
    );
  }, [onChangeWrapper, options, value]);
},
formioContainerPropsAreEqual);

/**
 * Update the FormInstance.
 * IMPORTANT!: Only alter the object value don't create a new one there is the risk of stale closure.
 *
 * @param value the FormInstance that has to be updated
 * @param record the changed data
 * @param postOrPutFormInstance the ajax request
 * @param url the url
 */
function update(
  value: FormInstance,
  record: FormInstanceContent,
  postOrPutFormInstance: DebouncedFunc<
    (
      params: {
        formInstance: FormInstance;
      } & DynamicRequestOptions
    ) => Promise<SuccessResponse<string>>
  >,
  url: string
): Promise<FormInstance> {
  console.group('Update formioContainer', value.formType);
  console.dir(value);
  console.dir(record);
  console.groupEnd();
  if (record.changed && record.isValid && postOrPutFormInstance) {
    //Always delete changed out, because otherwise you get cyclic exceptions when trying to make the ajax call
    (value.content as Partial<typeof value.content>).changed = undefined;
    value.content.isValid = record.isValid;
    value.content.data = record.data;
    return new Promise((resolve, reject) => {
      const result = postOrPutFormInstance({
        url: url,
        formInstance: {
          ...value,
          content: { ...value.content, changed: {} }
        }
      });
      if (!result) {
        resolve(value);
      } else {
        result
          .then(({ data }) => {
            console.debug('Id of Form-Instance:', data);
            resolve(value);
          })
          .catch(reject);
      }
    });
  } else if (!record.changed && !value.content.data) {
    //change from outside
    (value.content as Partial<typeof value.content>).changed = undefined;
    value.content.isValid = record.isValid;
    value.content.data = record.data;
  }

  return Promise.resolve(value);
}

export const simpleFormWithButton = {
  components: [
    {
      type: 'button',
      label: 'Submit',
      key: 'submit',
      size: 'md',
      block: false,
      action: 'submit',
      disableOnInvalid: true,
      theme: 'primary',
      input: true,
      multiple: false,
      protected: false,
      unique: false,
      persistent: false,
      hidden: false,
      clearOnHide: true,
      tableView: false,
      modalEdit: false,
      dataGridLabel: true,
      labelPosition: 'top',
      hideLabel: false,
      disabled: false,
      autofocus: false,
      dbIndex: false,
      calculateServer: false,
      widget: {
        type: 'input'
      },
      validateOn: 'change',
      validate: {
        required: false,
        customPrivate: false,
        strictDateValidation: false,
        multiple: false,
        unique: false
      },
      allowCalculateOverride: false,
      encrypted: false,
      showCharCount: false,
      showWordCount: false,
      allowMultipleMasks: false,
      id: 'ekwo3xl'
    },
    {
      label: 'Checkbox',
      inputType: 'checkbox',
      hidden: false,
      hideLabel: false,
      autofocus: false,
      disabled: false,
      tableView: false,
      modalEdit: false,
      persistent: true,
      protected: false,
      dbIndex: false,
      encrypted: false,
      clearOnHide: true,
      calculateServer: false,
      allowCalculateOverride: false,
      validate: {
        required: false,
        customPrivate: false,
        strictDateValidation: false,
        multiple: false,
        unique: false
      },
      key: 'checkbox',
      type: 'checkbox',
      input: true,
      multiple: false,
      unique: false,
      dataGridLabel: true,
      labelPosition: 'right',
      validateOn: 'change',
      showCharCount: false,
      showWordCount: false,
      allowMultipleMasks: false,
      id: 'ev3vs7j'
    }
  ]
};
